import { ForwardedRef, MutableRefObject, RefCallback, useMemo } from 'react';

export type Ref<T> = ForwardedRef<T> | MutableRefObject<T> | RefCallback<T>;

export const setRef = <T>(ref: Ref<T> | null, value: T) => {
  if (typeof ref === 'function') {
    ref(value);
  } else if (ref) {
    // eslint-disable-next-line no-param-reassign
    ref.current = value;
  }
};

/**
 * Given two Refs, returns a single RefCallback that will set
 * both of them when it receives a new value. This is useful in
 * scenarios where a ForwardRef and local ref need to be merged
 * into a single Ref for a node. See the example below:
 *
 * ```tsx
 * const Button = forwardRef(({ children, onClick }, ref) => {
 *    const buttonRef = useRef<HTMLButtonElement | null>(null);
 *    const mergedRef = useMergedRef(buttonRef, forwardRef);
 *
 *    return (
 *      <Button onClick={onClick} innerRef={mergedRef}>
 *        {children}
 *      </Button>
 *    );
 * });
 * ```
 */
export const useMergedRef = <T>(
  refA: Ref<T> | null,
  refB: Ref<T> | null,
): RefCallback<T> | null => {
  /**
   * This will create a new function if the ref props change and are defined.
   * This means react will call the old forkRef with `null` and the new forkRef
   * with the ref. Cleanup naturally emerges from this behavior.
   */
  const merged = useMemo(() => {
    if (refA == null && refB == null) {
      return null;
    }

    return (refValue: T | null) => {
      setRef(refA, refValue);
      setRef(refB, refValue);
    };
  }, [refA, refB]);

  return merged;
};
